import { Box, Paper, Stack, Typography } from '@mui/material';
import React, { useState } from 'react';
import { CustomButton } from '../../../layout/styledcomponents/CustomButton';
import UserRole from '../../../shared/AuthRoles';
import { FormValues } from '../../types/experience';
import { useNavigate } from 'react-router-dom';
import { FormProvider, useForm } from 'react-hook-form';
import FormTextField from '../../shared/FormTextField';
import { useCreateChatEnquiryMutation } from '../../../app/Slices/SevenDayApiSlice';
import { ChatEnquiry } from '../../../app/Slices/SevenDayApiTypes';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import toast from '../../shared/Toast';

interface ContactFormProps {
    experience: FormValues;
}

const Schema = z.object({
    message: z
        .string()
        .min(1, { message: 'Required' })
        .max(5000, { message: 'Message cannot exceed 5000 characters' }),
    isPrivate: z.boolean(),
});

const ContactForm = ({ experience }: ContactFormProps) => {
    const loggedInUser = UserRole();

    const [createChatEnquiry] = useCreateChatEnquiryMutation();

    type ContactForm = z.infer<typeof Schema>;

    const methods = useForm<ContactForm>({
        resolver: zodResolver(Schema),
        defaultValues: { message: '', isPrivate: false },
    });

    const { control, handleSubmit, watch } = methods;

    const [messageSending, setMessageSending] = useState<boolean>(false);
    const isPrivate = watch('isPrivate');

    const navigate = useNavigate();

    const onSubmit = async (data: any) => {
        try {
            if (!experience.id) return;

            setMessageSending(true);

            const body: ChatEnquiry = {
                moduleId: experience.id,
                message: data.message,
                isPrivate,
            };

            const response = await createChatEnquiry(body).unwrap();

            navigate(`/messages/${response?.channelId}`);
        } catch {
            toast.error(`Error sending message to host`);
        } finally {
            setMessageSending(false);
        }
    };

    const messagingUsers = experience?.owner?.messagingUsers;

    return (
        <>
            {loggedInUser?.role?.includes('guest') && (
                <FormProvider {...methods}>
                    <Paper
                        elevation={3}
                        sx={{
                            backgroundColor: 'form.background',
                            borderRadius: '12px',
                            padding: '20px',
                        }}
                    >
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <Stack>
                                <Typography
                                    component={'h2'}
                                    variant='body1'
                                    fontWeight={'bold'}
                                >
                                    Contact the Host
                                </Typography>

                                {messagingUsers && messagingUsers.length > 0 ? (
                                    <>
                                        <Typography
                                            variant='body3'
                                            sx={{ mt: 1.5 }}
                                        >
                                            Got a question about this
                                            experience? Send a message to the
                                            host for more details. Whether it's
                                            about availability, specific
                                            details, or special requests, the
                                            host is here to help!
                                        </Typography>
                                        <FormTextField
                                            placeholder="Hi! I'll be visiting..."
                                            name='message'
                                            control={control}
                                            rows={3}
                                            multiline
                                            sx={{
                                                mt: 1.5,
                                                backgroundColor: 'neutral.main',
                                            }}
                                        />

                                        <Box pt={1}>
                                            <CustomButton
                                                loading={messageSending}
                                                disabled={messageSending}
                                                variant='outlined'
                                                color='primary'
                                                type='submit'
                                                sx={{ fontSize: '12px' }}
                                            >
                                                Send message
                                            </CustomButton>
                                        </Box>
                                    </>
                                ) : (
                                    <Typography variant='body1' gutterBottom>
                                        This host does not allow for in-app
                                        messaging about this experience.
                                    </Typography>
                                )}
                            </Stack>
                        </form>
                    </Paper>
                </FormProvider>
            )}
        </>
    );
};

export default ContactForm;
